import {
  useCallback,
  useState,
} from 'react';

export type FinancialReportAccount = {
  id: string;
  name: string;

  settlementReceiptsCents:
    number;

  settlementReversalsCents:
    number;

  ledgerInCents:
    number;

  ledgerOutCents:
    number;

  netCents:
    number;
};

export type FinancialReportCategory = {
  id: string;
  name: string;
  categoryType: string;

  netCents:
    number;

  transactionCount:
    number;
};

export type FinancialReportChargeType = {
  chargeType: string;

  receivedCents:
    number;

  reversalCents:
    number;

  netReceivedCents:
    number;
};

export type FinancialReportMonth = {
  monthKey: string;

  settlementNetCents:
    number;

  operationalLedgerCents:
    number;

  transferLedgerCents:
    number;

  operationalResultCents:
    number;

  cashNetCents:
    number;
};

export type FinancialReport = {
  from: string;
  to: string;

  accountId:
    string | null;

  generatedAt: string;

  summary: {
    settlementReceiptsCents:
      number;

    settlementReversalsCents:
      number;

    netSettlementsCents:
      number;

    operationalLedgerCents:
      number;

    transferLedgerCents:
      number;

    operationalResultCents:
      number;

    cashNetCents:
      number;
  };

  receivables: {
    outstandingCents:
      number;

    overdueCents:
      number;

    overdueCount:
      number;
  };

  byAccount:
    FinancialReportAccount[];

  byCategory:
    FinancialReportCategory[];

  byChargeType:
    FinancialReportChargeType[];

  monthly:
    FinancialReportMonth[];
};

export function useFinancialReports() {
  const [
    financialReport,
    setFinancialReport,
  ] = useState<
    FinancialReport | null
  >(null);

  const [
    isLoadingFinancialReport,
    setIsLoadingFinancialReport,
  ] = useState(false);

  const [
    financialReportError,
    setFinancialReportError,
  ] = useState<string | null>(
    null
  );

  const loadFinancialReport =
    useCallback(
      async (
        input: {
          from: string;
          to: string;
          accountId?: string;
        }
      ) => {
        setIsLoadingFinancialReport(
          true
        );

        setFinancialReportError(
          null
        );

        try {
          const params =
            new URLSearchParams({
              from:
                input.from,

              to:
                input.to,
            });

          if (
            input.accountId
          ) {
            params.set(
              'accountId',
              input.accountId
            );
          }

          const response =
            await fetch(
              `/api/financial-reports/summary?${params.toString()}`,
              {
                credentials:
                  'include',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !data.report
          ) {
            throw new Error(
              data.error ||
                'Não foi possível gerar o relatório financeiro.'
            );
          }

          setFinancialReport(
            data.report
          );

          return (
            data.report as
              FinancialReport
          );
        } catch (error: any) {
          setFinancialReport(
            null
          );

          setFinancialReportError(
            error?.message ||
              'Não foi possível gerar o relatório financeiro.'
          );

          return null;
        } finally {
          setIsLoadingFinancialReport(
            false
          );
        }
      },
      []
    );

  return {
    financialReport,

    isLoadingFinancialReport,

    financialReportError,

    loadFinancialReport,
  };
}